import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowLeft, ChevronDown, Trophy, CreditCard, Users, Lock, X } from "lucide-react";

export const Navbar = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
      }
    }
  }, []);
  
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/" className="p-1.5 sm:p-2 hover:bg-slate-100 rounded-full transition-colors" data-testid="home-btn" title="Home">
            <ArrowLeft className="w-4 sm:w-5 h-4 sm:h-5 text-slate-600" />
          </Link>
          <Link to="/" className="flex items-center gap-1.5 sm:gap-2" data-testid="logo-link">
            <img src="/images/logo.png" alt="NokMechik" className="w-6 sm:w-8 h-6 sm:h-8 rounded-full object-cover" />
            <span className="text-lg sm:text-2xl font-bold text-slate-800 tracking-tight">NokMechik</span>
          </Link>
        </div>
        <div className="hidden md:flex items-center gap-6">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-slate-600 hover:text-slate-800 font-medium cursor-pointer outline-none" data-testid="explore-dropdown">
              Explore
              <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/competitions" className="flex items-center gap-3 cursor-pointer" data-testid="dropdown-competitions">
                  <Trophy className="w-4 h-4 text-purple-600" />
                  <div>
                    <p className="font-medium">Competitions</p>
                    <p className="text-xs text-slate-500">Reels, singing & more</p>
                  </div>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/payment" className="flex items-center gap-3 cursor-pointer" data-testid="dropdown-payment">
                  <CreditCard className="w-4 h-4 text-green-600" />
                  <div>
                    <p className="font-medium">Payment</p>
                    <p className="text-xs text-slate-500">Donate & support</p>
                  </div>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-4">
          {user ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 text-slate-600 hover:text-slate-800 font-medium cursor-pointer outline-none" data-testid="user-dropdown">
                  <div className="w-8 h-8 bg-terracotta/20 rounded-full flex items-center justify-center">
                    <span className="text-terracotta font-bold text-sm">{user.name?.charAt(0).toUpperCase()}</span>
                  </div>
                  <span className="hidden sm:inline">{user.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="dropdown-dashboard">
                      <Users className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 cursor-pointer" data-testid="dropdown-admin">
                        <Lock className="w-4 h-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="logout-btn">
                    <X className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600 hover:text-slate-800 text-sm sm:text-base px-2 sm:px-4" data-testid="login-btn">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-3 sm:px-6 text-xs sm:text-sm" data-testid="get-started-btn">
                  Get Started
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
